import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountBalance as AssetIcon,
  Add as AddIcon,
  AttachMoney as MoneyIcon,
  AutoGraph as NBVIcon,
  CheckCircle as ActiveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useConfirm } from '../../hooks/useConfirm';
import { toast } from 'react-toastify';
import { fixedAssetsService } from '../../services/fixedAssets.service';
import {
  AssetStatus,
  AssetType,
  DisposalType,
  DisposeAssetDto,
  FixedAsset,
} from '../../types/fixedAssets.types';
import FixedAssetDialog from '../../components/fixedAssets/FixedAssetDialog';

// ─── Translations ─────────────────────────────────────────────────────────────

const translations = {
  en: {
    title: 'Fixed Assets',
    addAsset: 'New Asset',
    refresh: 'Refresh',
    edit: 'Edit',
    delete: 'Delete',
    viewSchedule: 'View Schedule',
    dispose: 'Dispose Asset',
    save: 'Save',
    cancel: 'Cancel',
    loading: 'Loading...',
    noData: 'No assets found',
    confirmDelete: 'Are you sure you want to delete this asset? This action cannot be undone.',
    // Stats
    totalAssets: 'Total Assets',
    activeAssets: 'Active Assets',
    totalCost: 'Total Cost (KWD)',
    totalNBV: 'Net Book Value (KWD)',
    // Filters
    search: 'Search by code or name...',
    filterType: 'Asset Type',
    filterStatus: 'Status',
    allTypes: 'All Types',
    allStatuses: 'All Statuses',
    // Asset types
    tangible: 'Tangible',
    intangible: 'Intangible',
    // Status labels
    active: 'Active',
    fullyDepreciated: 'Fully Depreciated',
    disposed: 'Disposed',
    // Table headers
    assetCode: 'Code',
    assetName: 'Asset Name',
    assetType: 'Type',
    category: 'Category',
    acquisitionDate: 'Acq. Date',
    cost: 'Cost',
    accumDep: 'Accum. Dep.',
    netBookValue: 'Net Book Value',
    status: 'Status',
    actions: 'Actions',
    // Methods
    straightLine: 'Straight Line',
    decliningBalance: 'Declining Balance',
    // Dispose dialog
    disposeTitle: 'Dispose Asset',
    disposalDate: 'Disposal Date',
    disposalType: 'Disposal Type',
    saleProceeds: 'Sale Proceeds (KWD)',
    notes: 'Notes',
    sale: 'Sale',
    writeOff: 'Write-Off',
    donation: 'Donation',
    disposalDateRequired: 'Disposal date is required',
    // Toast
    deletedSuccess: 'Asset deleted successfully',
    deleteFailed: 'Failed to delete asset',
    disposedSuccess: 'Asset disposed successfully',
    disposeFailed: 'Failed to dispose asset',
    rowsPerPage: 'Rows per page:',
  },
  ar: {
    title: 'الأصول الثابتة',
    addAsset: 'أصل جديد',
    refresh: 'تحديث',
    edit: 'تعديل',
    delete: 'حذف',
    viewSchedule: 'جدول الإهلاك',
    dispose: 'استبعاد الأصل',
    save: 'حفظ',
    cancel: 'إلغاء',
    loading: 'جاري التحميل...',
    noData: 'لا توجد أصول',
    confirmDelete: 'هل أنت متأكد من حذف هذا الأصل؟ لا يمكن التراجع عن هذا الإجراء.',
    // Stats
    totalAssets: 'إجمالي الأصول',
    activeAssets: 'الأصول النشطة',
    totalCost: 'إجمالي التكلفة (د.ك)',
    totalNBV: 'صافي القيمة الدفترية (د.ك)',
    // Filters
    search: 'بحث بالكود أو الاسم...',
    filterType: 'نوع الأصل',
    filterStatus: 'الحالة',
    allTypes: 'جميع الأنواع',
    allStatuses: 'جميع الحالات',
    // Asset types
    tangible: 'ملموسة',
    intangible: 'غير ملموسة',
    // Status labels
    active: 'نشط',
    fullyDepreciated: 'مستهلك بالكامل',
    disposed: 'مستبعد',
    // Table headers
    assetCode: 'الكود',
    assetName: 'اسم الأصل',
    assetType: 'النوع',
    category: 'الفئة',
    acquisitionDate: 'تاريخ الاقتناء',
    cost: 'التكلفة',
    accumDep: 'مجمع الإهلاك',
    netBookValue: 'صافي القيمة الدفترية',
    status: 'الحالة',
    actions: 'الإجراءات',
    // Methods
    straightLine: 'القسط الثابت',
    decliningBalance: 'القسط المتناقص',
    // Dispose dialog
    disposeTitle: 'استبعاد الأصل',
    disposalDate: 'تاريخ الاستبعاد',
    disposalType: 'نوع الاستبعاد',
    saleProceeds: 'عائد البيع (د.ك)',
    notes: 'ملاحظات',
    sale: 'بيع',
    writeOff: 'شطب',
    donation: 'تبرع',
    disposalDateRequired: 'تاريخ الاستبعاد مطلوب',
    // Toast
    deletedSuccess: 'تم حذف الأصل بنجاح',
    deleteFailed: 'فشل حذف الأصل',
    disposedSuccess: 'تم استبعاد الأصل بنجاح',
    disposeFailed: 'فشل استبعاد الأصل',
    rowsPerPage: 'عدد الصفوف:',
  },
};

// ─── Dispose form state ───────────────────────────────────────────────────────

interface DisposeFormState {
  disposalDate: string;
  disposalType: DisposalType;
  saleProceeds: string;
  notes: string;
}

const defaultDisposeForm: DisposeFormState = {
  disposalDate: new Date().toISOString().slice(0, 10),
  disposalType: 'Sale',
  saleProceeds: '',
  notes: '',
};

// ─── Component ────────────────────────────────────────────────────────────────

const FixedAssetsPage: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const confirm = useConfirm();
  const navigate = useNavigate();

  // ── Data state ─────────────────────────────────────────────────────────────
  const [assets, setAssets] = useState<FixedAsset[]>([]);
  const [filtered, setFiltered] = useState<FixedAsset[]>([]);
  const [loading, setLoading] = useState(false);

  // ── Filter state ───────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<AssetType | 'All'>('All');
  const [statusFilter, setStatusFilter] = useState<AssetStatus | 'All'>('All');

  // ── Pagination ─────────────────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ── Asset dialog ───────────────────────────────────────────────────────────
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<FixedAsset | undefined>();

  // ── Dispose dialog ─────────────────────────────────────────────────────────
  const [disposeDialogOpen, setDisposeDialogOpen] = useState(false);
  const [disposingAsset, setDisposingAsset] = useState<FixedAsset | null>(null);
  const [disposeForm, setDisposeForm] = useState<DisposeFormState>(defaultDisposeForm);
  const [disposeDateError, setDisposeDateError] = useState('');
  const [disposing, setDisposing] = useState(false);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const totalCount = assets.length;
  const activeCount = assets.filter((a) => a.status === 'Active').length;
  const totalCost = assets.reduce((sum, a) => sum + a.acquisitionCost, 0);
  const totalNBV = assets.reduce((sum, a) => sum + a.currentBookValue, 0);

  // ── Load ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadAssets();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [assets, searchTerm, typeFilter, statusFilter]);

  const loadAssets = async () => {
    setLoading(true);
    try {
      const data = await fixedAssetsService.getAll();
      setAssets(data);
    } catch {
      toast.error(isRTL ? 'فشل تحميل الأصول' : 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...assets];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (a) =>
          a.assetCode.toLowerCase().includes(term) ||
          a.assetName.toLowerCase().includes(term) ||
          a.assetNameAr.toLowerCase().includes(term)
      );
    }
    if (typeFilter !== 'All') {
      result = result.filter((a) => a.assetType === typeFilter);
    }
    if (statusFilter !== 'All') {
      result = result.filter((a) => a.status === statusFilter);
    }
    setFiltered(result);
    setPage(0);
  };

  // ── Asset dialog handlers ──────────────────────────────────────────────────
  const handleAdd = () => {
    setSelectedAsset(undefined);
    setAssetDialogOpen(true);
  };

  const handleEdit = (asset: FixedAsset) => {
    setSelectedAsset(asset);
    setAssetDialogOpen(true);
  };

  const handleAssetDialogClose = () => {
    setAssetDialogOpen(false);
    setSelectedAsset(undefined);
  };

  const handleAssetSaved = () => {
    loadAssets();
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async (asset: FixedAsset) => {
    const ok = await confirm({ message: t.confirmDelete, severity: 'error' });
    if (!ok) return;
    try {
      await fixedAssetsService.deleteAsset(asset.fixedAssetId);
      toast.success(t.deletedSuccess);
      loadAssets();
    } catch {
      toast.error(t.deleteFailed);
    }
  };

  // ── Schedule navigation ────────────────────────────────────────────────────
  const handleViewSchedule = (asset: FixedAsset) => {
    navigate(`/fixed-assets/${asset.fixedAssetId}/schedule`);
  };

  // ── Dispose dialog ─────────────────────────────────────────────────────────
  const openDisposeDialog = (asset: FixedAsset) => {
    setDisposingAsset(asset);
    setDisposeForm(defaultDisposeForm);
    setDisposeDateError('');
    setDisposeDialogOpen(true);
  };

  const closeDisposeDialog = () => {
    setDisposeDialogOpen(false);
    setDisposingAsset(null);
  };

  const handleDisposeFormChange = (field: keyof DisposeFormState, value: string) => {
    setDisposeForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'disposalDate' && disposeDateError) {
      setDisposeDateError('');
    }
  };

  const handleDisposeTypeChange = (e: SelectChangeEvent<DisposalType>) => {
    const type = e.target.value as DisposalType;
    setDisposeForm((prev) => ({
      ...prev,
      disposalType: type,
      saleProceeds: type !== 'Sale' ? '' : prev.saleProceeds,
    }));
  };

  const handleDisposeSave = async () => {
    if (!disposeForm.disposalDate) {
      setDisposeDateError(t.disposalDateRequired);
      return;
    }
    if (!disposingAsset) return;
    setDisposing(true);
    try {
      const dto: DisposeAssetDto = {
        disposalDate: disposeForm.disposalDate,
        disposalType: disposeForm.disposalType,
        saleProceeds:
          disposeForm.disposalType === 'Sale' && disposeForm.saleProceeds !== ''
            ? Number(disposeForm.saleProceeds)
            : 0,
        notes: disposeForm.notes || undefined,
      };
      await fixedAssetsService.dispose(disposingAsset.fixedAssetId, dto);
      toast.success(t.disposedSuccess);
      closeDisposeDialog();
      loadAssets();
    } catch {
      toast.error(t.disposeFailed);
    } finally {
      setDisposing(false);
    }
  };

  // ── Pagination ─────────────────────────────────────────────────────────────
  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // ── Chip helpers ───────────────────────────────────────────────────────────
  const statusChip = (status: AssetStatus) => {
    const map: Record<AssetStatus, { label: string; color: 'success' | 'info' | 'default' }> = {
      Active: { label: t.active, color: 'success' },
      FullyDepreciated: { label: t.fullyDepreciated, color: 'info' },
      Disposed: { label: t.disposed, color: 'default' },
    };
    const { label, color } = map[status];
    return <Chip label={label} color={color} size="small" sx={{ fontWeight: 'bold' }} />;
  };

  const typeChip = (type: AssetType) => (
    <Chip
      label={type === 'Tangible' ? t.tangible : t.intangible}
      size="small"
      sx={{
        fontWeight: 'bold',
        bgcolor: type === 'Tangible' ? 'rgba(25, 118, 210, 0.12)' : 'rgba(156, 39, 176, 0.12)',
        color: type === 'Tangible' ? '#1565c0' : '#7b1fa2',
      }}
    />
  );

  const fmtNumber = (n: number) =>
    n.toLocaleString(isRTL ? 'ar-KW' : 'en-KW', { minimumFractionDigits: 3, maximumFractionDigits: 3 });

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>

      {/* ── Stat Cards ── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {/* Total */}
        <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 180 }}>
          <Card
            elevation={4}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-5px)' },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{totalCount}</Typography>
                  <Typography variant="body2">{t.totalAssets}</Typography>
                </Box>
                <AssetIcon sx={{ fontSize: 56, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Active */}
        <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 180 }}>
          <Card
            elevation={4}
            sx={{
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-5px)' },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">{activeCount}</Typography>
                  <Typography variant="body2">{t.activeAssets}</Typography>
                </Box>
                <ActiveIcon sx={{ fontSize: 56, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Total Cost */}
        <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 180 }}>
          <Card
            elevation={4}
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-5px)' },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h5" fontWeight="bold">{fmtNumber(totalCost)}</Typography>
                  <Typography variant="body2">{t.totalCost}</Typography>
                </Box>
                <MoneyIcon sx={{ fontSize: 56, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Total NBV */}
        <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 180 }}>
          <Card
            elevation={4}
            sx={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'translateY(-5px)' },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h5" fontWeight="bold">{fmtNumber(totalNBV)}</Typography>
                  <Typography variant="body2">{t.totalNBV}</Typography>
                </Box>
                <NBVIcon sx={{ fontSize: 56, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* ── Header + Filters ── */}
      <Paper elevation={3} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssetIcon sx={{ fontSize: 32, color: '#667eea' }} />
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#667eea' }}>
              {t.title}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title={t.refresh}>
              <IconButton
                color="primary"
                onClick={loadAssets}
                sx={{
                  bgcolor: 'rgba(102, 126, 234, 0.1)',
                  '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              sx={{
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
                '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' },
              }}
            >
              {t.addAsset}
            </Button>
          </Stack>
        </Box>

        {/* Filter row */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder={t.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ flex: 2, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ flex: 1, minWidth: 150 }}>
            <InputLabel>{t.filterType}</InputLabel>
            <Select
              value={typeFilter}
              label={t.filterType}
              onChange={(e: SelectChangeEvent<AssetType | 'All'>) =>
                setTypeFilter(e.target.value as AssetType | 'All')
              }
            >
              <MenuItem value="All">{t.allTypes}</MenuItem>
              <MenuItem value="Tangible">{t.tangible}</MenuItem>
              <MenuItem value="Intangible">{t.intangible}</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ flex: 1, minWidth: 180 }}>
            <InputLabel>{t.filterStatus}</InputLabel>
            <Select
              value={statusFilter}
              label={t.filterStatus}
              onChange={(e: SelectChangeEvent<AssetStatus | 'All'>) =>
                setStatusFilter(e.target.value as AssetStatus | 'All')
              }
            >
              <MenuItem value="All">{t.allStatuses}</MenuItem>
              <MenuItem value="Active">{t.active}</MenuItem>
              <MenuItem value="FullyDepreciated">{t.fullyDepreciated}</MenuItem>
              <MenuItem value="Disposed">{t.disposed}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* ── Table ── */}
      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#667eea' }} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.assetCode}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.assetName}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.assetType}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.category}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.acquisitionDate}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{t.cost}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{t.accumDep}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">{t.netBookValue}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.status}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">{t.actions}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">{t.noData}</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((asset) => {
                  const nbv = asset.currentBookValue;
                  const canEdit = asset.status !== 'Disposed';
                  const canDispose = asset.status === 'Active' || asset.status === 'FullyDepreciated';
                  const canDelete = asset.status === 'Active';

                  return (
                    <TableRow
                      key={asset.fixedAssetId}
                      hover
                      sx={{ '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.05)' } }}
                    >
                      {/* Code */}
                      <TableCell>
                        <Chip
                          label={asset.assetCode}
                          size="small"
                          sx={{
                            fontWeight: 'bold',
                            bgcolor: 'rgba(102, 126, 234, 0.12)',
                            color: '#667eea',
                          }}
                        />
                      </TableCell>

                      {/* Name */}
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {isRTL ? asset.assetNameAr : asset.assetName}
                        </Typography>
                      </TableCell>

                      {/* Type */}
                      <TableCell>{typeChip(asset.assetType)}</TableCell>

                      {/* Category */}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {isRTL
                            ? (asset.categoryNameAr || asset.categoryName || '-')
                            : (asset.categoryName || '-')}
                        </Typography>
                      </TableCell>

                      {/* Acquisition Date */}
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(asset.acquisitionDate).toLocaleDateString(
                            isRTL ? 'ar-KW' : 'en-GB'
                          )}
                        </Typography>
                      </TableCell>

                      {/* Cost */}
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {fmtNumber(asset.acquisitionCost)}
                        </Typography>
                      </TableCell>

                      {/* Accumulated Depreciation */}
                      <TableCell align="right">
                        <Typography variant="body2" color="error.main">
                          {fmtNumber(asset.totalDepreciationToDate)}
                        </Typography>
                      </TableCell>

                      {/* Net Book Value */}
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ color: nbv > 0 ? 'success.main' : 'text.disabled' }}
                        >
                          {fmtNumber(nbv)}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell align="center">{statusChip(asset.status)}</TableCell>

                      {/* Actions */}
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {/* View Schedule */}
                          <Tooltip title={t.viewSchedule}>
                            <IconButton
                              size="small"
                              onClick={() => handleViewSchedule(asset)}
                              sx={{
                                color: '#667eea',
                                '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.1)' },
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {/* Edit */}
                          <Tooltip title={t.edit}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(asset)}
                                disabled={!canEdit}
                                sx={{
                                  color: 'primary.main',
                                  '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.1)' },
                                  '&.Mui-disabled': { opacity: 0.35 },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          {/* Dispose */}
                          {canDispose && (
                            <Tooltip title={t.dispose}>
                              <IconButton
                                size="small"
                                onClick={() => openDisposeDialog(asset)}
                                sx={{
                                  color: 'error.main',
                                  '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Delete */}
                          {canDelete && (
                            <Tooltip title={t.delete}>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(asset)}
                                sx={{
                                  color: 'text.secondary',
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t.rowsPerPage}
          labelDisplayedRows={({ from, to, count }) =>
            isRTL ? `${from}-${to} من ${count}` : `${from}-${to} of ${count}`
          }
        />
      </TableContainer>

      {/* ── Asset Create/Edit Dialog ── */}
      <FixedAssetDialog
        open={assetDialogOpen}
        onClose={handleAssetDialogClose}
        editAsset={selectedAsset}
        onSave={handleAssetSaved}
      />

      {/* ── Dispose Dialog ── */}
      <Dialog
        open={disposeDialogOpen}
        onClose={closeDisposeDialog}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        dir={isRTL ? 'rtl' : 'ltr'}
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 2,
          }}
        >
          <DeleteIcon />
          <Typography variant="h6" fontWeight={700} component="span">
            {t.disposeTitle}
            {disposingAsset && (
              <Typography component="span" variant="body2" sx={{ opacity: 0.85, ml: 1 }}>
                — {disposingAsset.assetCode}
              </Typography>
            )}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2.5, overflowY: 'auto' }}>
          {/* Disposal Date */}
          <TextField
            label={t.disposalDate}
            type="date"
            value={disposeForm.disposalDate}
            onChange={(e) => handleDisposeFormChange('disposalDate', e.target.value)}
            fullWidth
            size="small"
            error={!!disposeDateError}
            helperText={disposeDateError}
            InputLabelProps={{ shrink: true }}
          />

          {/* Disposal Type */}
          <FormControl fullWidth size="small">
            <InputLabel>{t.disposalType}</InputLabel>
            <Select<DisposalType>
              value={disposeForm.disposalType}
              label={t.disposalType}
              onChange={handleDisposeTypeChange}
            >
              <MenuItem value="Sale">{t.sale}</MenuItem>
              <MenuItem value="WriteOff">{t.writeOff}</MenuItem>
              <MenuItem value="Donation">{t.donation}</MenuItem>
            </Select>
          </FormControl>

          {/* Sale Proceeds — only if Sale */}
          {disposeForm.disposalType === 'Sale' && (
            <TextField
              label={t.saleProceeds}
              type="number"
              value={disposeForm.saleProceeds}
              onChange={(e) => handleDisposeFormChange('saleProceeds', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ min: 0, step: 0.001 }}
            />
          )}

          {/* Notes */}
          <TextField
            label={t.notes}
            value={disposeForm.notes}
            onChange={(e) => handleDisposeFormChange('notes', e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={3}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" color="inherit" onClick={closeDisposeDialog} disabled={disposing}>
            {t.cancel}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDisposeSave}
            disabled={disposing}
          >
            {disposing ? <CircularProgress size={20} color="inherit" /> : t.dispose}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FixedAssetsPage;
